import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuotationFollowUpOutcome, QuotationStatus, Region } from '@kalachand/shared';
import { DeliveryDatePicker } from '../../../components/DeliveryDatePicker';
import { api, apiGet, apiPatch, apiPost, getApiError } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { Modal } from '../../../components/Modal';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { cn } from '../../../lib/cn';
import { discountPctFromAmount, formatPct } from '../../../lib/discount';
import { emptyPayment, type PaymentDraft } from '../../../components/PaymentFields';
import { PaymentCapture, paymentsTotal, paymentsValid } from '../../../components/PaymentCapture';

interface QuotationDetail {
  id: string;
  number: string;
  status: QuotationStatus;
  vatExempt: boolean;
  subtotal: string;
  vatTotal: string;
  discountTotal: string;
  fastTrackFee: string;
  total: string;
  validUntil: string;
  notes: string | null;
  createdAt: string;
  wonAt: string | null;
  lostAt: string | null;
  lostReason: string | null;
  shop: { id: string; code: string; name: string };
  customer: {
    id: string;
    name: string;
    phone: string;
    email: string | null;
    type: 'individual' | 'company';
    companyName: string | null;
    contactPerson: string | null;
    vatNumber: string | null;
    brn: string | null;
    address: string | null;
    city: string | null;
    region: Region | null;
  };
  salesperson: { id: string; name: string };
  lines: {
    id: string;
    qty: number;
    unitPrice: string;
    discount: string;
    vatRate: string;
    lineTotal: string;
    /** Snapshot at quote time. For a custom-size line `name` holds the bespoke
     *  "Model — Custom W×L cm" label rather than the matched variant's name. */
    productSnapshot: { name?: string; sku?: string; customPricedAs?: string } | null;
    /** Bespoke mattress dimensions (cm); null for standard sizes. */
    customWidthCm: number | null;
    customLengthCm: number | null;
    product: {
      id: string;
      sku: string;
      name: string;
      category: { name: string; fulfillmentStrategy: string; deliveryRequired: boolean } | null;
      stockLevels: {
        onHand: number;
        reserved: number;
        display: number;
        location: { kind: string; shopId: string | null };
      }[];
    };
    /** Current available stock for stock-tracked products; null when the product
     *  isn't stock-tracked (made-to-order / supplier). Set by GET /quotations/:id. */
    available: number | null;
    outOfStock: boolean;
  }[];
  followUps: {
    id: string;
    outcome: QuotationFollowUpOutcome;
    note: string | null;
    nextFollowUpAt: string | null;
    createdAt: string;
    user: { id: string; name: string };
  }[];
  convertedOrder: { id: string; number: string; status: string } | null;
}

const OUTCOMES: { value: QuotationFollowUpOutcome; label: string }[] = [
  { value: 'CALLED', label: 'Called' },
  { value: 'NO_ANSWER', label: 'No answer' },
  { value: 'CALLBACK_REQUESTED', label: 'Callback requested' },
  { value: 'NOT_INTERESTED', label: 'Not interested' },
  { value: 'WHATSAPP_SENT', label: 'WhatsApp sent' },
  { value: 'EMAIL_SENT', label: 'Email sent' },
  { value: 'OTHER', label: 'Other' },
];

function outcomeLabel(o: string): string {
  return OUTCOMES.find((x) => x.value === o)?.label ?? o;
}

function statusTone(
  s: QuotationStatus,
): 'positive' | 'warning' | 'negative' | 'neutral' | 'accent' {
  switch (s) {
    case 'WON':
      return 'positive';
    case 'LOST':
      return 'negative';
    case 'EXPIRED':
      return 'neutral';
    case 'FOLLOWED_UP':
      return 'accent';
    default:
      return 'warning';
  }
}

function daysBetween(target: Date, now: Date = new Date()): number {
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function QuotationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [convertOpen, setConvertOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);

  const quoteQuery = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => apiGet<QuotationDetail>(`/quotations/${id}`),
  });

  const addFollowUp = useMutation({
    mutationFn: (body: { outcome: QuotationFollowUpOutcome; note: string | null }) =>
      apiPost(`/quotations/${id}/follow-ups`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotation', id] }),
  });

  const markStatus = useMutation({
    mutationFn: (body: { status: 'WON' | 'LOST'; lostReason?: string }) =>
      apiPatch(`/quotations/${id}/status`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotation', id] }),
  });

  if (quoteQuery.isLoading)
    return (
      <PageScaffold back={{ to: '/quotations', label: 'Quotations' }}>
        <div className="text-center text-ink-3 py-20">Loading…</div>
      </PageScaffold>
    );
  if (!quoteQuery.data)
    return (
      <PageScaffold back={{ to: '/quotations', label: 'Quotations' }}>
        <div className="text-center text-ink-3 py-20">Quote not found.</div>
      </PageScaffold>
    );
  const q = quoteQuery.data;

  const display =
    q.customer.type === 'company' ? (q.customer.companyName ?? q.customer.name) : q.customer.name;
  const validUntil = new Date(q.validUntil);
  const days = daysBetween(validUntil);
  const isLive = q.status === 'PENDING' || q.status === 'FOLLOWED_UP';
  const expiringSoon = isLive && days >= 0 && days <= 5;
  const expired = q.status === 'EXPIRED' || (isLive && days < 0);

  const handlePdf = (mode: 'download' | 'inline'): void => {
    // The Authorization header isn't sent for window.open, so fetch + blob:
    void (async () => {
      try {
        const res = await api.get(`/quotations/${id}/pdf`, {
          params: mode === 'download' ? { download: 'true' } : {},
          responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (mode === 'inline') {
          const w = window.open(url, '_blank');
          if (w) setTimeout(() => w.print(), 500);
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${q.number}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  return (
    <PageScaffold
      back={{ to: '/quotations', label: 'Quotations' }}
      title={
        <span className="inline-flex items-center gap-3 align-middle flex-wrap">
          {q.number}
          <StatusPill tone={statusTone(q.status)}>
            {q.status.replace('_', ' ').toLowerCase()}
          </StatusPill>
          {q.vatExempt && <StatusPill tone="info">VAT excluded</StatusPill>}
        </span>
      }
      subtitle={
        <>
          Quoted to{' '}
          <Link to={`/customers/${q.customer.id}`} className="text-ink hover:text-accent">
            {display}
          </Link>{' '}
          · {q.shop.name} · by {q.salesperson.name}
        </>
      }
      actions={
        <>
          <Button variant="ghost" onClick={() => handlePdf('inline')}>
            Print
          </Button>
          <Button variant="secondary" onClick={() => handlePdf('download')}>
            Download PDF
          </Button>
          <EmailDocButton
            endpoint={`/quotations/${id}/email`}
            defaultEmail={q.customer.email}
            title="Email quotation"
            subtitle="Send the quotation PDF to the customer."
          />
          {isLive && (
            <>
              <Button variant="secondary" onClick={() => setLostOpen(true)}>
                Mark lost
              </Button>
              <Button onClick={() => setConvertOpen(true)}>Accept &amp; convert to order</Button>
            </>
          )}
          {q.convertedOrder && (
            <Link to={`/orders/${q.convertedOrder.id}`}>
              <Button>Open order {q.convertedOrder.number} →</Button>
            </Link>
          )}
        </>
      }
    >
      {/* Validity banner */}
      <Card
        className={cn(
          'border',
          expired
            ? 'border-negative/40 bg-negative-soft'
            : expiringSoon
              ? 'border-warning/40 bg-warning-soft'
              : 'border-rule',
        )}
        bodyClassName="p-4"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div
              className={cn(
                'text-[10px] uppercase tracking-[0.22em] font-medium',
                expired ? 'text-negative' : expiringSoon ? 'text-warning' : 'text-ink-3',
              )}
            >
              {expired ? 'Expired' : 'Valid until'}
            </div>
            <div className={cn('kc-serif text-2xl mt-0.5', expired ? 'text-negative' : 'text-ink')}>
              {validUntil.toLocaleDateString('en-MU', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>
          </div>
          {isLive && (
            <div className="text-sm">
              {expired ? (
                <span className="text-negative">
                  Expired {-days} day{-days === 1 ? '' : 's'} ago
                </span>
              ) : expiringSoon ? (
                <span className="text-warning">
                  Only {days} day{days === 1 ? '' : 's'} left
                </span>
              ) : (
                <span className="text-ink-3">{days} days remaining</span>
              )}
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — lines + totals + notes */}
        <div className="lg:col-span-2 space-y-6">
          {q.lines.some((l) => l.outOfStock) && (
            <div className="flex items-start gap-2 rounded-md border border-warning/20 bg-warning-soft px-3 py-2 text-sm text-warning">
              <span aria-hidden>⚠</span>
              <span>
                {q.lines.filter((l) => l.outOfStock).length === 1
                  ? 'An item on this quotation is'
                  : `${q.lines.filter((l) => l.outOfStock).length} items on this quotation are`}{' '}
                currently out of stock. The quote is still valid — stock is checked when it's
                converted to an order.
              </span>
            </div>
          )}
          <Card
            title="Line items"
            subtitle={`${q.lines.length} item${q.lines.length === 1 ? '' : 's'}`}
            bodyClassName="p-0"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">Product</th>
                  <th className="px-6 py-2 font-medium text-right">Qty</th>
                  <th className="px-6 py-2 font-medium text-right">Unit</th>
                  <th className="px-6 py-2 font-medium text-right">Discount</th>
                  <th className="px-6 py-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {q.lines.map((l) => {
                  const disc = Number(l.discount);
                  const pct = discountPctFromAmount(l.qty, Number(l.unitPrice), disc);
                  const isCustom = l.customWidthCm != null;
                  // Custom lines display the bespoke label from the snapshot, not
                  // the matched standard variant's name.
                  const displayName = l.productSnapshot?.name ?? l.product.name;
                  return (
                    <tr key={l.id} className="border-b border-rule-2">
                      <td className="px-6 py-3">
                        <div className="text-ink flex items-center gap-2">
                          <span>{displayName}</span>
                          {isCustom && (
                            <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-accent bg-accent-soft px-1.5 py-0.5 rounded">
                              Custom
                            </span>
                          )}
                          {l.outOfStock && (
                            <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-warning bg-warning-soft border border-warning/20 rounded px-1.5 py-0.5">
                              Out of stock
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-ink-3 kc-mono">
                          {isCustom
                            ? `Priced as ${l.productSnapshot?.customPricedAs ?? l.product.sku}`
                            : l.product.sku}
                        </div>
                        {l.product.category && (
                          <div className="text-[10px] text-ink-3 mt-0.5">
                            {l.product.category.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right kc-mono">{l.qty}</td>
                      <td className="px-6 py-3 text-right kc-mono">
                        <Money value={l.unitPrice} />
                      </td>
                      <td className="px-6 py-3 text-right kc-mono text-ink-3">
                        {disc > 0 ? (
                          <div className="leading-tight">
                            <Money value={l.discount} className="text-current" />
                            <div className="text-[10px] text-ink-4">{formatPct(pct)}</div>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-6 py-3 text-right kc-mono">
                        <Money value={l.lineTotal} className="text-ink" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-rule">
                  <td
                    colSpan={4}
                    className="px-6 py-3 text-right text-xs uppercase tracking-wide text-ink-3"
                  >
                    Subtotal
                  </td>
                  <td className="px-6 py-3 text-right kc-mono">
                    <Money value={q.subtotal} className="text-ink" />
                  </td>
                </tr>
                {Number(q.discountTotal) > 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-1 text-right text-xs uppercase tracking-wide text-ink-3"
                    >
                      Discount
                    </td>
                    <td className="px-6 py-1 text-right kc-mono text-warning">
                      − <Money value={q.discountTotal} className="text-current" />
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-1 text-right text-xs uppercase tracking-wide text-ink-3"
                  >
                    VAT
                  </td>
                  <td className="px-6 py-1 text-right kc-mono text-ink">
                    {q.vatExempt ? (
                      <span className="text-ink-3 text-xs italic">Exempt</span>
                    ) : (
                      <Money value={q.vatTotal} className="text-ink" />
                    )}
                  </td>
                </tr>
                {Number(q.fastTrackFee) > 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-1 text-right text-xs uppercase tracking-wide text-ink-3"
                    >
                      Fast Track Delivery
                    </td>
                    <td className="px-6 py-1 text-right kc-mono text-ink">
                      <Money value={q.fastTrackFee} className="text-ink" />
                    </td>
                  </tr>
                )}
                <tr className="border-t border-rule">
                  <td colSpan={4} className="px-6 py-3 text-right text-sm font-medium text-ink">
                    TOTAL
                  </td>
                  <td className="px-6 py-3 text-right kc-mono">
                    <Money value={q.total} className="kc-serif text-2xl text-accent" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </Card>

          {q.notes && (
            <Card title="Notes">
              <p className="text-sm text-ink whitespace-pre-line">{q.notes}</p>
            </Card>
          )}

          {q.lostReason && (
            <Card title="Lost reason" className="border-negative/30">
              <p className="text-sm text-ink">{q.lostReason}</p>
              {q.lostAt && (
                <p className="text-xs text-ink-3 mt-2">
                  Marked lost {new Date(q.lostAt).toLocaleDateString('en-MU')}
                </p>
              )}
            </Card>
          )}
        </div>

        {/* RIGHT — customer card + follow-ups */}
        <div className="space-y-6">
          <Card title="Customer">
            <div className="text-sm">
              <div className="font-medium text-ink">{display}</div>
              {q.customer.type === 'company' && q.customer.contactPerson && (
                <div className="text-xs text-ink-3 mt-0.5">Attn: {q.customer.contactPerson}</div>
              )}
              <div className="text-xs text-ink-3 kc-mono mt-2">{q.customer.phone}</div>
              {q.customer.address && (
                <div className="text-xs text-ink-3 mt-1">
                  {[q.customer.address, q.customer.city].filter(Boolean).join(', ')}
                </div>
              )}
              {q.customer.type === 'company' && (q.customer.vatNumber || q.customer.brn) && (
                <div className="text-xs text-ink-3 mt-2 kc-mono">
                  {[
                    q.customer.vatNumber && `VAT ${q.customer.vatNumber}`,
                    q.customer.brn && `BRN ${q.customer.brn}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
            <Link
              to={`/customers/${q.customer.id}`}
              className="text-xs text-accent hover:underline block mt-3"
            >
              View full customer record →
            </Link>
          </Card>

          <FollowUpCard
            quoteId={q.id}
            followUps={q.followUps}
            disabled={!isLive}
            disabledReason={
              q.status === 'WON'
                ? 'Quote already accepted'
                : q.status === 'LOST'
                  ? 'Quote already marked lost'
                  : q.status === 'EXPIRED'
                    ? 'Quote expired'
                    : undefined
            }
            onAdd={(body) => addFollowUp.mutate(body)}
            busy={addFollowUp.isPending}
          />
        </div>
      </div>

      {/* Convert to order modal */}
      {convertOpen && (
        <ConvertModal
          quote={q}
          onClose={() => setConvertOpen(false)}
          onDone={(orderId) => {
            setConvertOpen(false);
            navigate(`/orders/${orderId}`);
          }}
        />
      )}

      {/* Mark lost modal */}
      {lostOpen && (
        <MarkLostModal
          onClose={() => setLostOpen(false)}
          busy={markStatus.isPending}
          onSubmit={(reason) => {
            markStatus.mutate(
              { status: 'LOST', lostReason: reason },
              { onSuccess: () => setLostOpen(false) },
            );
          }}
        />
      )}
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FollowUpCard({
  quoteId: _qid,
  followUps,
  disabled,
  disabledReason,
  onAdd,
  busy,
}: {
  quoteId: string;
  followUps: QuotationDetail['followUps'];
  disabled: boolean;
  disabledReason?: string;
  onAdd: (body: { outcome: QuotationFollowUpOutcome; note: string | null }) => void;
  busy: boolean;
}): JSX.Element {
  const [outcome, setOutcome] = useState<QuotationFollowUpOutcome>('CALLED');
  const [note, setNote] = useState('');

  const submit = (): void => {
    onAdd({ outcome, note: note || null });
    setNote('');
  };

  return (
    <Card
      title="Follow-ups"
      subtitle={`${followUps.length} log entr${followUps.length === 1 ? 'y' : 'ies'}`}
    >
      {disabled ? (
        <div className="text-xs text-ink-3 italic mb-3">{disabledReason}</div>
      ) : (
        <div className="space-y-3 mb-4 pb-4 border-b border-rule">
          <label className="block">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Outcome</span>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as QuotationFollowUpOutcome)}
              className="kc-input mt-1 w-full"
            >
              {OUTCOMES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Note (optional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="What did they say?"
              className="kc-input mt-1 w-full resize-none"
            />
          </label>
          <Button onClick={submit} loading={busy} className="w-full">
            Log follow-up
          </Button>
        </div>
      )}

      {followUps.length === 0 ? (
        <p className="text-xs text-ink-3 italic">No follow-ups yet.</p>
      ) : (
        <ol className="space-y-3">
          {followUps.map((f) => (
            <li key={f.id} className="border-l-2 border-accent pl-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-ink">{outcomeLabel(f.outcome)}</span>
                <span className="text-[10px] text-ink-3 kc-mono">
                  {new Date(f.createdAt).toLocaleDateString('en-MU', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              {f.note && <p className="text-xs text-ink-2 mt-0.5 whitespace-pre-line">{f.note}</p>}
              <div className="text-[10px] text-ink-3 mt-1">by {f.user.name}</div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function MarkLostModal({
  onClose,
  busy,
  onSubmit,
}: {
  onClose: () => void;
  busy: boolean;
  onSubmit: (reason: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title="Mark quotation as lost"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(reason)} loading={busy}>
            Confirm lost
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-3 mb-3">
        Optionally add why the customer declined. This helps the team learn over time.
      </p>
      <label className="block">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
          Reason (optional)
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Price too high · Went with competitor · Postponed…"
          className="kc-input mt-1 w-full resize-none"
        />
      </label>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface ShopRow {
  id: string;
  code: string;
  name: string;
  type: 'HQ' | 'SHOP' | 'ONLINE';
}

function ConvertModal({
  quote,
  onClose,
  onDone,
}: {
  quote: QuotationDetail;
  onClose: () => void;
  onDone: (orderId: string) => void;
}): JSX.Element {
  const pickupBlocked = quote.lines.some((line) => line.product.category?.deliveryRequired);
  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'delivery'>(
    pickupBlocked ? 'delivery' : 'pickup',
  );
  const [pickupShopId, setPickupShopId] = useState(quote.shop.id);
  const [deliveryAddress, setDeliveryAddress] = useState(
    [quote.customer.address, quote.customer.city].filter(Boolean).join(', '),
  );
  const [deliveryDate, setDeliveryDate] = useState('');
  const [region, setRegion] = useState<Region | null>(quote.customer.region ?? null);
  const [disposalQty, setDisposalQty] = useState(0);
  const [notes, setNotes] = useState('');
  const [backorderProductIds, setBackorderProductIds] = useState<string[]>([]);
  const [allowBackorderReservation, setAllowBackorderReservation] = useState(false);
  const [payments, setPayments] = useState<PaymentDraft[]>(() => [emptyPayment('')]);
  const [createdOrder, setCreatedOrder] = useState<{ id: string; number: string } | null>(null);
  const [handoverNowLineIds, setHandoverNowLineIds] = useState<string[]>([]);
  const accessoryLines = quote.lines.filter(
    (l) =>
      l.product.category?.fulfillmentStrategy === 'STOCK_AT_LOCATION' &&
      !l.product.category.deliveryRequired &&
      l.customWidthCm == null &&
      l.customLengthCm == null,
  );
  const availableAtSellingShop = (line: QuotationDetail['lines'][number]) =>
    (line.product.stockLevels ?? [])
      .filter((level) => level.location.shopId === quote.shop.id)
      .reduce((sum, level) => sum + Math.max(0, level.onHand - level.reserved - level.display), 0);
  const selectedHandoverLineIds = accessoryLines
    .filter((line) => !backorderProductIds.includes(line.product.id) && handoverNowLineIds.includes(line.id) && availableAtSellingShop(line) >= line.qty)
    .map((line) => line.id);

  const shopsQuery = useQuery({ queryKey: ['shops'], queryFn: () => apiGet<ShopRow[]>('/shops') });

  // A cross-shop converter (owner/HQ/ops) selling for a branch SHOP must attribute
  // the sale to one of that shop's sales girls (drives commission). A sales girl
  // converting her own quote sells as herself — the backend resolves that, so the
  // picker only shows for the cross-shop + branch-shop case. Defaults to the
  // quote's own salesperson (valid when a branch girl raised the quote).
  const { perms, user } = useAuth();
  const quoteShop = shopsQuery.data?.find((s) => s.id === quote.shop.id);
  const needsSalesperson = perms.has('orders.view_all_shops') && quoteShop?.type === 'SHOP';
  const [salespersonId, setSalespersonId] = useState(quote.salesperson.id);
  const salespeopleQuery = useQuery({
    queryKey: ['shop-salespeople', quote.shop.id],
    queryFn: () => apiGet<{ id: string; name: string }[]>(`/shops/${quote.shop.id}/salespeople`),
    enabled: needsSalesperson,
  });

  // Soonest date the factory can finish this order — the backend resolves which
  // lines are made-to-order and greys out days the convert would reject.
  const earliestQuery = useQuery({
    queryKey: ['earliest-delivery', 'quote', quote.id],
    queryFn: () =>
      apiPost<{ earliest: string | null }>('/scheduling/earliest-delivery', {
        lines: quote.lines.map((l) => ({ productId: l.product.id, qty: l.qty })),
      }),
    enabled: fulfillmentType === 'delivery',
  });
  const earliestDate = earliestQuery.data?.earliest ?? undefined;
  const total = Number(quote.total);
  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: () => apiGet<{ depositThresholdPct: string }>('/company'),
  });
  const depositPct = companyQuery.data ? Number(companyQuery.data.depositThresholdPct) : 0.3;
  const minDeposit = Math.round(total * depositPct * 100) / 100;
  const paymentReady =
    perms.has('payments.record') &&
    paymentsValid(payments, total) &&
    paymentsTotal(payments) + 0.01 >= minDeposit;

  const submit = useMutation({
    mutationFn: async () => {
      setCreatedOrder(null);
      const order = await apiPost<{ orderId: string; orderNumber: string }>(
        `/quotations/${quote.id}/convert`,
        {
          fulfillmentType,
          backorderProductIds: fulfillmentType === 'delivery' ? backorderProductIds : [],
          allowBackorderReservation: fulfillmentType === 'delivery' && allowBackorderReservation,
          handoverNowLineIds: fulfillmentType === 'delivery' ? selectedHandoverLineIds : [],
          ...(needsSalesperson && salespersonId ? { salespersonId } : {}),
          pickupShopId: fulfillmentType === 'pickup' ? pickupShopId : null,
          deliveryAddress: fulfillmentType === 'delivery' ? deliveryAddress : null,
          deliveryDate: fulfillmentType === 'delivery' && deliveryDate ? deliveryDate : null,
          region: fulfillmentType === 'delivery' ? region : null,
          disposalQty: fulfillmentType === 'delivery' ? disposalQty : 0,
          notes: notes || null,
        },
      );
      try {
        await apiPost(`/payments/orders/${order.orderId}/batch`, {
          payments: payments.map((payment) => ({
            amount: Number(payment.amount),
            method: payment.method,
            reference: payment.reference.trim() || null,
          })),
        });
      } catch (error) {
        // Conversion cannot be rolled back safely after the order exists. Keep
        // its identity so the user can finish payment from the order page.
        setCreatedOrder({ id: order.orderId, number: order.orderNumber });
        throw error;
      }
      return order;
    },
    onSuccess: (data) => onDone(data.orderId),
  });

  return (
    <Modal
      title="Convert quotation to order"
      subtitle={`${quote.number} · ${
        quote.customer.type === 'company'
          ? (quote.customer.companyName ?? quote.customer.name)
          : quote.customer.name
      }`}
      size="2xl"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={(needsSalesperson && !salespersonId) || !paymentReady}
            onClick={() => submit.mutate()}
          >
            Convert &amp; confirm
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-3 mb-4">
        Customer accepted — the quotation will become an order, and this payment will confirm it.
        Payment is recorded against the resulting order, not the quotation.
      </p>
      <div className="space-y-4">
        {needsSalesperson && (
          <div>
            <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
              Salesperson (for commission)
            </label>
            <select
              className="kc-input w-full"
              value={salespersonId}
              onChange={(e) => setSalespersonId(e.target.value)}
            >
              <option value="">Select a salesperson…</option>
              {(salespeopleQuery.data ?? []).map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-ink-3 mt-1">
              This sale is at {quote.shop.name} — attribute it to the sales girl who owns it.
            </p>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
            Fulfillment
          </label>
          <div className="flex gap-2">
            {(['pickup', 'delivery'] as const).map((t) => (
              <button
                key={t}
                type="button"
                disabled={t === 'pickup' && pickupBlocked}
                onClick={() => setFulfillmentType(t)}
                className={cn(
                  'flex-1 px-4 py-3 rounded border text-sm transition disabled:opacity-40 disabled:cursor-not-allowed',
                  fulfillmentType === t
                    ? 'bg-accent text-white border-accent'
                    : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                )}
              >
                {t === 'pickup' ? 'Pickup at shop' : 'Delivery'}
              </button>
            ))}
          </div>
          {pickupBlocked && (
            <p className="mt-2 text-xs text-ink-3">
              This quotation includes items that require delivery.
            </p>
          )}
        </div>

        {fulfillmentType === 'pickup' && (
          <label className="block">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
              Pickup shop
            </span>
            <select
              value={pickupShopId}
              onChange={(e) => setPickupShopId(e.target.value)}
              className="kc-input mt-1 w-full"
            >
              {(shopsQuery.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {fulfillmentType === 'delivery' && (
          <>
            {quote.lines.some((line) => line.available != null && line.customWidthCm == null && line.customLengthCm == null) && (
              <fieldset className="rounded border border-rule p-3 space-y-2">
                <legend className="text-sm font-medium">Reserve items for incoming stock</legend>
                <p className="text-xs text-ink-3">
                  Selected items will be reserved and delivered once stock arrives. The full
                  quantity of each selected item will wait for stock.
                </p>
                {quote.lines.filter((line) => line.available != null && line.customWidthCm == null && line.customLengthCm == null).map((line) => (
                  <label key={line.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={backorderProductIds.includes(line.product.id)}
                      onChange={(event) => setBackorderProductIds((ids) =>
                        event.target.checked
                          ? [...ids, line.product.id]
                          : ids.filter((id) => id !== line.product.id),
                      )}
                    />
                    {line.product.name} · {line.qty} requested · {line.available} available
                  </label>
                ))}
              </fieldset>
            )}
            {submit.isError && getApiError(submit.error).code === 'reservation_required' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={allowBackorderReservation}
                  onChange={(event) => setAllowBackorderReservation(event.target.checked)}
                />
                Reserve items short at the warehouse for delivery after the next arrival
              </label>
            )}
            <Input
              label="Delivery address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
            />
            <div>
              <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide mb-1.5">
                Delivery date
              </label>
              <DeliveryDatePicker
                region={region}
                onRegionChange={setRegion}
                value={deliveryDate}
                onChange={setDeliveryDate}
                earliestDate={earliestDate}
                customerNeedsRegion={!quote.customer.region}
                customerLabel={
                  quote.customer.type === 'company'
                    ? (quote.customer.companyName ?? quote.customer.name)
                    : quote.customer.name
                }
              />
            </div>
            <label className="block">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Old mattresses to collect
              </span>
              <input
                type="number"
                min="0"
                max="20"
                value={disposalQty}
                onChange={(e) =>
                  setDisposalQty(Math.max(0, Math.min(20, Number(e.target.value || 0))))
                }
                className="kc-input mt-1 w-24"
              />
              <span className="text-xs text-ink-3 ml-2">Free service · 0 = none</span>
            </label>
          </>
        )}

        {fulfillmentType === 'delivery' && accessoryLines.length > 0 && (
          <Card
            title="Small items"
            subtitle={`Choose what the customer takes from ${quote.shop.name} now.`}
          >
            <div className="space-y-3">
              {accessoryLines.map((line) => {
                const available = availableAtSellingShop(line);
                const canHandover = available >= line.qty && !backorderProductIds.includes(line.product.id);
                const checked = canHandover && handoverNowLineIds.includes(line.id);
                return (
                  <div key={line.id}>
                    <label
                      className={cn(
                        'flex items-center gap-2 text-sm',
                        canHandover ? 'cursor-pointer' : 'text-ink-3',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canHandover}
                        onChange={(e) =>
                          setHandoverNowLineIds((ids) =>
                            e.target.checked ? [...ids, line.id] : ids.filter((id) => id !== line.id),
                          )
                        }
                        className="accent-accent"
                      />
                      Hand over now — {line.product.name} × {line.qty}
                    </label>
                    <div className="mt-1 ml-5 text-xs text-ink-3">
                      {checked
                        ? `Customer takes this from ${quote.shop.name} when payment confirms the order.`
                        : backorderProductIds.includes(line.product.id)
                          ? 'Reserved for delivery after incoming stock arrives.'
                          : canHandover
                          ? `${available} available here. Leave unticked to send it from HQ warehouse with the delivery.`
                          : `Only ${available} available here; ${line.qty} needed. This item ships from HQ warehouse.`}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <label className="block">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Order notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything the order team should know…"
            className="kc-input mt-1 w-full resize-none"
          />
        </label>

        <Card
          title="Payment"
          subtitle={`Required — full amount, or at least a ${Math.round(depositPct * 100)}% deposit`}
        >
          {perms.has('payments.record') ? (
            <>
              <PaymentCapture
                lines={payments}
                onChange={setPayments}
                total={total}
                userName={user?.name}
                shopName={quote.shop.name}
                canRecordCheque={perms.has('payments.record_cheque')}
                depositOption={{
                  label: `Deposit ${Math.round(depositPct * 100)}%`,
                  value: Math.ceil(total * depositPct * 100) / 100,
                }}
              />
              {!paymentsValid(payments, total) ? (
                <div className="mt-2 text-xs text-ink-3">
                  Enter a payment to convert and confirm the quotation.
                </div>
              ) : paymentsTotal(payments) + 0.01 < minDeposit ? (
                <div className="mt-2 text-xs text-negative">
                  Deposit must be at least {Math.round(depositPct * 100)}% — Rs{' '}
                  {minDeposit.toLocaleString()}.
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-sm text-negative">
              You do not have permission to record payments.
            </div>
          )}
        </Card>

        {submit.isError && (
          <div className="text-sm text-negative">
            {createdOrder ? (
              <>
                Order {createdOrder.number} was created, but its payment was not recorded:{' '}
                {getApiError(submit.error).message}{' '}
                <Link className="underline" to={`/orders/${createdOrder.id}`}>
                  Open the order to finish payment.
                </Link>
              </>
            ) : (
              getApiError(submit.error).message
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
